/*!
 * AudoChat - Authentication Utility
 * Uses Supabase for authentication and user management
 */

import { supabase } from '../lib/supabase';
import api from './api';
import { PERMISSIONS } from './database';

// Store current user in localStorage (for session management)
const CURRENT_USER_KEY = "audochat_current_user";

export const auth = {
  // Check if user is authenticated
  isAuthenticated() {
    const user = this.getCurrentUser();
    return user !== null;
  },

  // Get current user from localStorage
  getCurrentUser() {
    try {
      const user = localStorage.getItem(CURRENT_USER_KEY);
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error("Error getting current user:", error);
      return null;
    }
  },

  // Set current user in localStorage
  setCurrentUser(user) {
    try {
      localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
      return true;
    } catch (error) {
      console.error("Error setting current user:", error);
      return false;
    }
  },

  // Check if user has permission
  hasPermission(permission) {
    const user = this.getCurrentUser();
    if (!user || !user.role) return false;

    const ROLE_PERMISSIONS = {
      agent: [
        PERMISSIONS.CHAT,
        PERMISSIONS.DASHBOARD,
        PERMISSIONS.CRM,
        PERMISSIONS.ITEMS,
        PERMISSIONS.BILLING,
      ],
      admin: [
        PERMISSIONS.CHAT,
        PERMISSIONS.DASHBOARD,
        PERMISSIONS.CRM,
        PERMISSIONS.ITEMS,
        PERMISSIONS.BILLING,
        PERMISSIONS.SETTINGS,
        PERMISSIONS.USER_MANAGEMENT,
      ],
    };

    const permissions = ROLE_PERMISSIONS[user.role] || [];
    return permissions.includes(permission);
  },

  // Check if user can access route
  canAccessRoute(route) {
    const user = this.getCurrentUser();
    if (!user) return false;

    // Map routes to permissions
    const routePermissions = {
      "/dashboard": PERMISSIONS.DASHBOARD,
      "/chat": PERMISSIONS.CHAT,
      "/tables": PERMISSIONS.CRM,
      "/items": PERMISSIONS.ITEMS,
      "/billing": PERMISSIONS.BILLING,
      "/settings": PERMISSIONS.SETTINGS,
    };

    const permission = routePermissions[route];
    if (!permission) return true; // Allow access to routes without specific permissions

    return this.hasPermission(permission);
  },

  // Login - uses API (which uses Supabase)
  async login(email, password) {
    try {
      const result = await api.login(email, password);
      
      if (result.success && result.user) {
        // Store user in localStorage
        this.setCurrentUser(result.user);
        return { success: true, user: result.user };
      }
      
      return { success: false, error: result.error || "Login failed" };
    } catch (error) {
      console.error("Login error:", error);
      return { 
        success: false, 
        error: error.message || "Network error. Please check if Supabase is accessible." 
      };
    }
  },

  // Login with Supabase Auth (alternative method)
  async loginWithSupabaseAuth(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user) {
        // Get user details from users table
        const userDetails = await api.getUserByEmail(email);
        if (userDetails) {
          this.setCurrentUser(userDetails);
          return { success: true, user: userDetails };
        }
      }

      return { success: false, error: "User not found" };
    } catch (error) {
      console.error("Supabase Auth login error:", error);
      return { 
        success: false, 
        error: error.message || "Login failed" 
      };
    }
  },

  // Sign up with Supabase Auth
  async signUp(email, password, userData = {}) {
    try {
      // First, sign up with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin + '/authentication/sign-in',
        },
      });

      if (authError) {
        return { success: false, error: authError.message };
      }

      if (authData.user) {
        // Create user in users table
        const user = await api.createUser({
          email,
          name: userData.name || email.split('@')[0],
          role: userData.role || 'agent',
          ...userData,
        });

        if (user) {
          this.setCurrentUser(user);
          return { success: true, user };
        }
      }

      return { success: false, error: "Failed to create user" };
    } catch (error) {
      console.error("Sign up error:", error);
      return { 
        success: false, 
        error: error.message || "Sign up failed" 
      };
    }
  },

  // Get current Supabase session
  async getSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    } catch (error) {
      console.error("Error getting session:", error);
      return null;
    }
  },

  // Listen to auth state changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange((event, session) => {
      callback(event, session);
    });
  },

  // Logout
  async logout() {
    try {
      // Sign out from Supabase Auth
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Supabase logout error:", error);
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear local storage
      localStorage.removeItem(CURRENT_USER_KEY);
      window.location.href = "/authentication/sign-in";
    }
  },
};

export default auth;
